import React, { useState, useEffect, useMemo } from 'react';
import { Plus, ShieldCheck, Lock, Trash2, Save, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import Modal from '../../components/ui/Modal';

const RolesPermissions = () => {
  const [modules, setModules]   = useState([]);
  const [roles, setRoles]       = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [perms, setPerms]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newRole, setNewRole]   = useState({ name: '', description: '' });

  const fetchAll = async () => {
    try {
      const [cat, rolesRes] = await Promise.all([
        api.get('/access/catalog'),
        api.get('/access/roles'),
      ]);
      setModules(cat.data.data.modules);
      setRoles(rolesRes.data.data);
      setSelectedId((prev) => prev || rolesRes.data.data[0]?._id || null);
    } catch (err) {
      toast.error('Failed to load access settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const selected = useMemo(() => roles.find((r) => r._id === selectedId), [roles, selectedId]);

  useEffect(() => {
    if (selected) setPerms(selected.permissions || []);
  }, [selected]);

  const dirty = useMemo(() => {
    if (!selected) return false;
    const a = [...(selected.permissions || [])].sort().join(',');
    const b = [...perms].sort().join(',');
    return a !== b;
  }, [selected, perms]);

  const editable = selected && !selected.locked;

  const togglePerm = (key) => {
    if (!editable) return;
    setPerms((prev) => prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]);
  };

  const toggleModule = (mod) => {
    if (!editable) return;
    const keys = mod.permissions.map((p) => p.key);
    const allOn = keys.every((k) => perms.includes(k));
    setPerms((prev) => allOn ? prev.filter((p) => !keys.includes(p)) : [...new Set([...prev, ...keys])]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/access/roles/${selected._id}`, { permissions: perms });
      toast.success(`Permissions updated for ${selected.name}`);
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const createRole = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/access/roles', { ...newRole, permissions: [] });
      toast.success('Role created');
      setShowCreate(false);
      setNewRole({ name: '', description: '' });
      await fetchAll();
      setSelectedId(res.data.data._id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create role');
    }
  };

  const deleteRole = async () => {
    if (!window.confirm(`Delete role "${selected.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/access/roles/${selected._id}`);
      toast.success('Role deleted');
      setSelectedId(null);
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete role');
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="zip-card h-96 skeleton" />
        <div className="zip-card h-96 skeleton lg:col-span-2" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      {/* Roles list */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-zip-text tracking-tight">Roles</h3>
          <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-1.5 text-xs py-1.5 px-3">
            <Plus size={13} /> New Role
          </button>
        </div>
        <div className="space-y-2">
          {roles.map((r) => (
            <button
              key={r._id}
              onClick={() => setSelectedId(r._id)}
              className={`w-full text-left zip-card p-4 transition-all duration-150 border-l-[3px]
                ${selectedId === r._id ? 'border-l-zip-red bg-zip-red/[0.03]' : 'border-l-transparent hover:bg-zip-hover'}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-zip-text text-sm flex items-center gap-1.5">
                  {r.locked && <Lock size={12} className="text-zip-muted" />}
                  {r.name}
                </span>
                {r.isSystem && <span className="chip-gray text-[10px]">Built-in</span>}
              </div>
              {r.description && <p className="text-xs text-zip-muted mt-1 line-clamp-2">{r.description}</p>}
              <div className="flex items-center gap-3 mt-2 text-[11px] font-mono text-zip-muted">
                <span className="flex items-center gap-1"><Users size={11} /> {r.userCount}</span>
                <span>{r.locked ? 'all' : (r.permissions?.length ?? 0)} permissions</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Permission editor */}
      <div className="lg:col-span-2">
        {!selected ? (
          <div className="zip-card p-10 text-center text-zip-muted text-sm">Select a role to manage its permissions.</div>
        ) : (
          <motion.div key={selected._id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="zip-card overflow-hidden">
            {/* Header */}
            <div className="px-5 py-4 border-b border-zip-border flex items-start justify-between gap-3">
              <div>
                <h3 className="font-semibold text-zip-text tracking-tight flex items-center gap-2">
                  <ShieldCheck size={16} className="text-zip-red" /> {selected.name}
                </h3>
                <p className="text-xs text-zip-muted mt-0.5">{selected.description || 'No description'}</p>
              </div>
              <div className="flex items-center gap-2">
                {!selected.isSystem && (
                  <button onClick={deleteRole} className="btn-ghost text-zip-red hover:bg-red-50 flex items-center gap-1.5 text-xs">
                    <Trash2 size={13} /> Delete
                  </button>
                )}
                <button
                  onClick={save}
                  disabled={!editable || !dirty || saving}
                  className="btn-primary flex items-center gap-1.5 text-xs disabled:opacity-50"
                >
                  <Save size={13} /> {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>

            {selected.locked ? (
              <div className="p-8 text-center">
                <Lock size={22} className="text-zip-muted mx-auto mb-2" />
                <p className="text-sm text-zip-text font-medium">Full access — locked</p>
                <p className="text-xs text-zip-muted mt-1">The Super Admin role always has every permission and cannot be edited.</p>
              </div>
            ) : (
              <div className="p-5 space-y-5 max-h-[calc(100vh-260px)] overflow-y-auto">
                {modules.map((mod) => {
                  const keys = mod.permissions.map((p) => p.key);
                  const allOn = keys.every((k) => perms.includes(k));
                  const someOn = keys.some((k) => perms.includes(k));
                  return (
                    <div key={mod.key} className="border border-zip-border rounded-xl overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-2.5 bg-zip-surface border-b border-zip-border">
                        <span className="text-sm font-semibold text-zip-text">{mod.label}</span>
                        <button
                          type="button"
                          onClick={() => toggleModule(mod)}
                          className={`text-[11px] font-mono px-2 py-0.5 rounded transition-colors
                            ${allOn ? 'text-zip-red hover:bg-red-50' : someOn ? 'text-amber-600 hover:bg-amber-50' : 'text-zip-muted hover:bg-zip-hover'}`}
                        >
                          {allOn ? 'Clear all' : 'Select all'}
                        </button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                        {mod.permissions.map((p) => (
                          <label key={p.key} className="flex items-center gap-2.5 px-4 py-2.5 cursor-pointer hover:bg-zip-hover transition-colors">
                            <input
                              type="checkbox"
                              className="accent-zip-red w-4 h-4 flex-shrink-0"
                              checked={perms.includes(p.key)}
                              onChange={() => togglePerm(p.key)}
                            />
                            <span className="min-w-0">
                              <span className="block text-sm text-zip-text">{p.label}</span>
                              <span className="block text-[11px] font-mono text-zip-muted/70 truncate">{p.key}</span>
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Create role modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create New Role">
        <form onSubmit={createRole} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-zip-muted mb-1.5">Role Name *</label>
            <input className="zip-input" required placeholder="e.g. Warehouse Manager"
              value={newRole.name} onChange={(e) => setNewRole({ ...newRole, name: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-medium text-zip-muted mb-1.5">Description</label>
            <textarea className="zip-input resize-none" rows={2} placeholder="What this role is for…"
              value={newRole.description} onChange={(e) => setNewRole({ ...newRole, description: e.target.value })} />
          </div>
          <p className="text-[11px] text-zip-muted/70">You'll pick its permissions after it's created.</p>
          <div className="flex gap-3 pt-1">
            <button type="submit" className="btn-primary flex-1">Create Role</button>
            <button type="button" onClick={() => setShowCreate(false)} className="btn-ghost">Cancel</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default RolesPermissions;
